import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiRequestsService } from '../../services/api-requests.service';

declare const bootstrap: any;

interface L1Master {
    _id: string;
    masterName: string;
    fieldLabel: string;
    description: string;
    userTypeScope: 'internal' | 'external' | 'none' | 'disabled';
}

interface L2Value {
    _id: string;
    masterL1Id: string;
    value: string;
    createdAt: string;
}

@Component({
    selector: 'app-generic-master',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './generic-master.component.html',
    styleUrl: './generic-master.component.scss',
})
export class GenericMasterComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('addL1Modal') addL1ModalRef?: ElementRef<HTMLElement>;
    @ViewChild('editL1Modal') editL1ModalRef?: ElementRef<HTMLElement>;
    @ViewChild('addL2Modal') addL2ModalRef?: ElementRef<HTMLElement>;
    @ViewChild('editL2Modal') editL2ModalRef?: ElementRef<HTMLElement>;

    masters: L1Master[] = [];
    selectedMaster: L1Master | null = null;
    values: L2Value[] = [];

    isLoadingMasters = false;
    isLoadingValues = false;
    isSavingL1 = false;
    isSavingL2 = false;

    errorMessage = '';
    successMessage = '';

    newL1 = { masterName: '', fieldLabel: '', description: '', userTypeScope: 'none' as 'internal' | 'external' | 'none' | 'disabled' };
    editL1 = { _id: '', masterName: '', fieldLabel: '', description: '', userTypeScope: 'none' as 'internal' | 'external' | 'none' | 'disabled' };

    newL2Value = '';
    editL2 = { _id: '', value: '' };

    private addL1Modal: any;
    private editL1ModalInst: any;
    private addL2Modal: any;
    private editL2ModalInst: any;

    constructor(private apiService: ApiRequestsService) {}

    ngOnInit(): void { this.loadMasters(); }

    ngAfterViewInit(): void {
        [this.addL1ModalRef, this.editL1ModalRef, this.addL2ModalRef, this.editL2ModalRef].forEach(ref => {
            if (ref?.nativeElement) document.body.appendChild(ref.nativeElement);
        });
        if (this.addL1ModalRef) this.addL1Modal = new bootstrap.Modal(this.addL1ModalRef.nativeElement);
        if (this.editL1ModalRef) this.editL1ModalInst = new bootstrap.Modal(this.editL1ModalRef.nativeElement);
        if (this.addL2ModalRef) this.addL2Modal = new bootstrap.Modal(this.addL2ModalRef.nativeElement);
        if (this.editL2ModalRef) this.editL2ModalInst = new bootstrap.Modal(this.editL2ModalRef.nativeElement);
    }

    ngOnDestroy(): void {
        [this.addL1Modal, this.editL1ModalInst, this.addL2Modal, this.editL2ModalInst].forEach(m => m?.hide());
        [this.addL1ModalRef, this.editL1ModalRef, this.addL2ModalRef, this.editL2ModalRef].forEach(ref => {
            const el = ref?.nativeElement;
            if (el?.parentNode === document.body) document.body.removeChild(el);
        });
    }

    loadMasters(): void {
        this.isLoadingMasters = true;
        this.apiService.getApiCall('api/v1/generic-master').subscribe({
            next: (res: any) => {
                this.isLoadingMasters = false;
                this.masters = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
            },
            error: () => { this.isLoadingMasters = false; },
        });
    }

    selectMaster(m: L1Master): void {
        this.selectedMaster = m;
        this.values = [];
        this.isLoadingValues = true;
        this.apiService.getApiCall(`api/v1/generic-master/${m._id}/values`).subscribe({
            next: (res: any) => {
                this.isLoadingValues = false;
                this.values = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
            },
            error: () => { this.isLoadingValues = false; },
        });
    }

    // ── Level 1 ──────────────────────────────────────────────────────────────

    openAddL1(): void {
        this.newL1 = { masterName: '', fieldLabel: '', description: '', userTypeScope: 'none' };
        this.errorMessage = '';
        this.addL1Modal?.show();
    }

    saveL1(): void {
        if (!this.newL1.masterName.trim()) { this.errorMessage = 'Master name is required.'; return; }
        this.isSavingL1 = true;
        this.errorMessage = '';
        const body = {
            masterName: this.newL1.masterName.trim(),
            fieldLabel: this.newL1.fieldLabel.trim() || this.newL1.masterName.trim(),
            description: this.newL1.description.trim(),
            userTypeScope: this.newL1.userTypeScope,
        };
        this.apiService.postApiCall('api/v1/generic-master', body).subscribe({
            next: (res: any) => {
                this.isSavingL1 = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.addL1Modal?.hide();
                    this.successMessage = `Master "${body.masterName}" created.`;
                    this.loadMasters();
                } else {
                    this.errorMessage = res?.errorDescription || 'Failed to create master.';
                }
            },
            error: () => { this.isSavingL1 = false; this.errorMessage = 'Failed to create master.'; },
        });
    }

    openEditL1(m: L1Master): void {
        this.editL1 = { _id: m._id, masterName: m.masterName, fieldLabel: m.fieldLabel, description: m.description || '', userTypeScope: m.userTypeScope || 'none' };
        this.errorMessage = '';
        this.editL1ModalInst?.show();
    }

    updateL1(): void {
        if (!this.editL1.masterName.trim()) { this.errorMessage = 'Master name is required.'; return; }
        this.isSavingL1 = true;
        this.errorMessage = '';
        const body = {
            masterName: this.editL1.masterName.trim(),
            fieldLabel: this.editL1.fieldLabel.trim() || this.editL1.masterName.trim(),
            description: this.editL1.description.trim(),
            userTypeScope: this.editL1.userTypeScope,
        };
        this.apiService.putApiCall(`api/v1/generic-master/${this.editL1._id}`, body).subscribe({
            next: (res: any) => {
                this.isSavingL1 = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.editL1ModalInst?.hide();
                    this.successMessage = 'Master updated.';
                    if (this.selectedMaster?._id === this.editL1._id) {
                        this.selectedMaster = { ...this.selectedMaster, ...body };
                    }
                    this.loadMasters();
                } else {
                    this.errorMessage = res?.errorDescription || 'Failed to update master.';
                }
            },
            error: () => { this.isSavingL1 = false; this.errorMessage = 'Failed to update master.'; },
        });
    }

    deactivateL1(m: L1Master): void {
        if (!confirm(`Remove master "${m.masterName}" and all its values? This cannot be undone.`)) return;
        this.apiService.deleteApiCall(`api/v1/generic-master/${m._id}`).subscribe({
            next: (res: any) => {
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.successMessage = `Master "${m.masterName}" removed.`;
                    if (this.selectedMaster?._id === m._id) { this.selectedMaster = null; this.values = []; }
                    this.loadMasters();
                }
            },
            error: () => {},
        });
    }

    // ── Level 2 ──────────────────────────────────────────────────────────────

    openAddL2(): void {
        this.newL2Value = '';
        this.errorMessage = '';
        this.addL2Modal?.show();
    }

    saveL2(): void {
        if (!this.newL2Value.trim()) { this.errorMessage = 'Value is required.'; return; }
        this.isSavingL2 = true;
        this.errorMessage = '';
        this.apiService.postApiCall(`api/v1/generic-master/${this.selectedMaster!._id}/values`, { value: this.newL2Value.trim() }).subscribe({
            next: (res: any) => {
                this.isSavingL2 = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.addL2Modal?.hide();
                    this.successMessage = `"${this.newL2Value.trim()}" added.`;
                    this.selectMaster(this.selectedMaster!);
                } else {
                    this.errorMessage = res?.errorDescription || 'Failed to add value.';
                }
            },
            error: () => { this.isSavingL2 = false; this.errorMessage = 'Failed to add value.'; },
        });
    }

    openEditL2(v: L2Value): void {
        this.editL2 = { _id: v._id, value: v.value };
        this.errorMessage = '';
        this.editL2ModalInst?.show();
    }

    updateL2(): void {
        if (!this.editL2.value.trim()) { this.errorMessage = 'Value is required.'; return; }
        this.isSavingL2 = true;
        this.errorMessage = '';
        this.apiService.putApiCall(
            `api/v1/generic-master/${this.selectedMaster!._id}/values/${this.editL2._id}`,
            { value: this.editL2.value.trim() }
        ).subscribe({
            next: (res: any) => {
                this.isSavingL2 = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.editL2ModalInst?.hide();
                    this.successMessage = 'Value updated.';
                    this.selectMaster(this.selectedMaster!);
                } else {
                    this.errorMessage = res?.errorDescription || 'Failed to update value.';
                }
            },
            error: () => { this.isSavingL2 = false; this.errorMessage = 'Failed to update value.'; },
        });
    }

    deactivateL2(v: L2Value): void {
        if (!confirm(`Remove "${v.value}"?`)) return;
        this.apiService.deleteApiCall(`api/v1/generic-master/${this.selectedMaster!._id}/values/${v._id}`).subscribe({
            next: (res: any) => {
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.successMessage = `"${v.value}" removed.`;
                    this.selectMaster(this.selectedMaster!);
                }
            },
            error: () => {},
        });
    }
}
